import { useCallback, useEffect, useRef, useState } from "react";
import { amountToPct, pctToAmount, formatAmount } from "../util";

type TVerticalInvestmentSliderProps = {
  amountState: number;
  minAmount?: number;
  maxAmount?: number;
  onChange?: (amount: number) => void;
};

const BASE_CLASS = "verticalInvestmentSilder";

const VerticalInvestmentSlider = ({
  amountState,
  onChange,
}: TVerticalInvestmentSliderProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [pct, setPct] = useState(() => amountToPct(amountState));
  const [dragging, setDragging] = useState(false);

  const updateFromClientY = useCallback(
    (clientY: number) => {
      if (!trackRef.current) return;

      const rect = trackRef.current.getBoundingClientRect();
      const nextPct =
        (1 - (clientY - rect.top) / rect.height) * 100;

      const clamped = Math.min(99, Math.max(1, nextPct));
      setPct(clamped);

      onChange?.(pctToAmount(clamped));
    },
    [onChange]
  );

  useEffect(() => {
    setPct(amountToPct(amountState));
  }, [amountState]);

  useEffect(() => {
    if (!dragging) return;

    const move = (e: MouseEvent) => updateFromClientY(e.clientY);
    const up = () => setDragging(false);

    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);

    return () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
  }, [dragging, updateFromClientY]);

  const amount = pctToAmount(pct);

  return (
    <div className={BASE_CLASS}>
      <div
        ref={trackRef}
      className={`${BASE_CLASS}__track`}
        onMouseDown={(e) => {
          setDragging(true);
          updateFromClientY(e.clientY);
        }}
      >
        <div
          className={`${BASE_CLASS}__handle`}
          style={{
            bottom: `${pct}%`,
            boxShadow: dragging
              ? "0 4px 16px rgba(204,0,0,.65)"
              : "0 2px 10px rgba(204,0,0,.5)",
          }}
        >
          <div className={`${BASE_CLASS}__tooltip`}
            style={{ opacity: dragging ? 1 : 0 }}>
            Su inversión
            <br />
            <strong>{formatAmount(amount)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerticalInvestmentSlider;